import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import SafeIcon from '../SafeIcon';
import {
  FiGrid, FiCornerUpLeft, FiCornerUpRight, FiShoppingBag, FiUploadCloud, FiCheck,
  FiLoader, FiAlertCircle, FiActivity, FiZap
} from 'react-icons/fi';
import StyleMenuPopup from './StyleMenuPopup';
import SettingsPopup from './SettingsPopup';

const TopNav = ({
  view,
  activeWebsiteName,
  onOpenSites,
  onOpenBuilder,
  onOpenStore,
  onOpenDashboard,
  menuStyles,
  onMenuStylesChange,
  onUndo,
  onRedo,
  canUndo,
  canRedo,
  onPublish,
  isPublished,
  isPublishing,
  publishError,
}) => {
  const [showStyleMenu, setShowStyleMenu] = useState(false);
  const [showSettings, setShowSettings] = useState(false);

  return (
    <div className="bg-white border-b border-gray-100 flex items-center justify-between px-6 py-3 shrink-0 z-50">
      {/* Left: Logo + Project Name */}
      <div className="flex items-center space-x-4">
        <div className="flex items-center space-x-3">
          <div className="w-8 h-8 bg-[#c8f542] rounded-lg flex items-center justify-center">
            <span className="text-black font-black text-sm">S</span>
          </div>
          <span className="font-black text-gray-900 text-lg">SideHustlr</span>
        </div>
        <div className="w-px h-6 bg-gray-200" />
        <button
          onClick={onOpenSites}
          className="flex items-center space-x-2 px-3 py-1.5 rounded-lg hover:bg-gray-50 transition-colors group"
        >
          <SafeIcon icon={FiGrid} className="w-4 h-4 text-gray-400 group-hover:text-gray-600" />
          <span className="text-sm font-bold text-gray-600 group-hover:text-gray-900 max-w-[200px] truncate">
            {activeWebsiteName || 'Select Project'}
          </span>
        </button>
      </div>

      {/* Center: View Tabs */}
      <div className="flex items-center space-x-2">
        <button
          onClick={onOpenBuilder}
          className={`px-4 py-2 rounded-xl font-bold text-xs uppercase tracking-widest transition-all ${
            view === 'builder'
              ? 'bg-gray-900 text-white shadow-lg'
              : 'text-gray-500 hover:text-gray-700 hover:bg-gray-50'
          }`}
        >
          Builder
        </button>
        <button
          onClick={onOpenStore}
          className={`px-4 py-2 rounded-xl font-bold text-xs uppercase tracking-widest transition-all flex items-center space-x-2 ${
            view === 'store'
              ? 'bg-gray-900 text-white shadow-lg'
              : 'text-gray-500 hover:text-gray-700 hover:bg-gray-50'
          }`}
        >
          <SafeIcon icon={FiShoppingBag} className="w-3.5 h-3.5" />
          <span>Store</span>
        </button>
        {onOpenDashboard && (
          <button
            onClick={onOpenDashboard}
            className={`px-4 py-2 rounded-xl font-bold text-xs uppercase tracking-widest transition-all flex items-center space-x-2 ${
              view === 'dashboard'
                ? 'bg-[#c8f542] text-black shadow-lg'
                : 'text-gray-500 hover:text-gray-700 hover:bg-gray-50'
            }`}
          >
            <SafeIcon icon={FiZap} className="w-3.5 h-3.5" />
            <span>Dashboard</span>
          </button>
        )}
      </div>

      {/* Right: Actions */}
      <div className="flex items-center space-x-3">
        {view === 'builder' && (
          <>
            <div className="flex items-center space-x-1 bg-gray-50 rounded-lg p-1">
              <button
                onClick={onUndo}
                disabled={!canUndo}
                className="p-2 rounded-md hover:bg-white transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
                title="Undo (Ctrl+Z)"
              >
                <SafeIcon icon={FiCornerUpLeft} className="w-4 h-4 text-gray-600" />
              </button>
              <button
                onClick={onRedo}
                disabled={!canRedo}
                className="p-2 rounded-md hover:bg-white transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
                title="Redo (Ctrl+Y)"
              >
                <SafeIcon icon={FiCornerUpRight} className="w-4 h-4 text-gray-600" />
              </button>
            </div>
            <button
              onClick={() => setShowStyleMenu(true)}
              className="px-4 py-2 bg-gray-50 hover:bg-gray-100 text-gray-700 font-bold rounded-xl text-xs uppercase tracking-widest transition-all"
            >
              Menu Styles
            </button>
          </>
        )}

        {onPublish && (
          <button
            onClick={onPublish}
            disabled={isPublishing}
            className={`px-5 py-2.5 rounded-xl font-black text-xs uppercase tracking-widest transition-all flex items-center space-x-2 shadow-lg ${
              isPublished
                ? 'bg-green-500 hover:bg-green-600 text-white'
                : 'bg-[#c8f542] hover:bg-[#d4ff50] text-black'
            } disabled:opacity-50 disabled:cursor-not-allowed`}
          >
            {isPublishing ? (
              <>
                <SafeIcon icon={FiLoader} className="w-4 h-4 animate-spin" />
                <span>Publishing...</span>
              </>
            ) : isPublished ? (
              <>
                <SafeIcon icon={FiCheck} className="w-4 h-4" />
                <span>Published</span>
              </>
            ) : (
              <>
                <SafeIcon icon={FiUploadCloud} className="w-4 h-4" />
                <span>Publish</span>
              </>
            )}
          </button>
        )}
      </div>

      {/* Style Menu Popup */}
      <AnimatePresence>
        {showStyleMenu && (
          <StyleMenuPopup
            currentStyles={menuStyles}
            onUpdate={onMenuStylesChange}
            onClose={() => setShowStyleMenu(false)}
          />
        )}
      </AnimatePresence>

      {/* Settings Popup */}
      <AnimatePresence>
        {showSettings && (
          <SettingsPopup onClose={() => setShowSettings(false)} />
        )}
      </AnimatePresence>
    </div>
  );
};

export default TopNav;